import React from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  dialogPaperSx,
  orderSummaryDialogPaperSx,
  imageContainerSx,
  dialogContentBoxSx,
  titleTypographySx,
  orderSummaryTitleTypographySx,
  messageTypographySx,
  dialogActionsSx,
  orangeOutlinedButtonSx,
  orangeContainedButtonSx,
  outlinedButtonSx,
  containedOkButtonSx,
  containedPrimaryButtonSx,
  iconStyle,
  truckIconStyle,
} from "./paymentDialog.styles";
import backArrow from "../assets/icons/orange-back-arrow.svg";
import whiteTruck from "../assets/icons/white-truck-icon.svg";
import Stroller from "../assets/Stroller.svg";
import { useLocation } from "react-router-dom";

interface PaymentDialogProps {
  open: boolean;
  onClose?: () => void;
  title: string | JSX.Element;
  message: React.ReactNode;
  primaryAction?: () => void;
  secondaryAction?: () => void;
  primaryText?: string;
  secondaryText?: string;
  imageUrl?: string;
}

const PaymentDialog: React.FC<PaymentDialogProps> = ({
  open,
  onClose,
  title,
  message,
  primaryAction,
  secondaryAction,
  primaryText = "Track your order",
  secondaryText,
  imageUrl,
}) => {
  const location = useLocation();
  const isCartPage = location.pathname.includes("cart");
  const isOrderSummaryPage = location.pathname.includes("seller/order-summary");
  const hasSecondary = Boolean(secondaryText);

  const getDialogPaperSx = () => {
    return isOrderSummaryPage ? orderSummaryDialogPaperSx : dialogPaperSx;
  };

  const getTitleSx = () => {
    return isOrderSummaryPage
      ? orderSummaryTitleTypographySx
      : titleTypographySx;
  };

  const getButtonStyles = () => {
    if (isOrderSummaryPage) {
      return {
        outlined: orangeOutlinedButtonSx,
        contained: orangeContainedButtonSx,
      };
    }

    if (primaryText === "Ok") {
      return {
        outlined: outlinedButtonSx,
        contained: containedOkButtonSx,
      };
    }

    return {
      outlined: outlinedButtonSx,
      contained: containedPrimaryButtonSx,
    };
  };

  const buttonStyles = getButtonStyles();
  const showIcon = primaryText !== "Ok" && !isOrderSummaryPage;
  const iconSrc = isCartPage ? Stroller : whiteTruck;
  const iconAlt = isCartPage ? "stroller" : "truck";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{ sx: getDialogPaperSx() }}
    >
      {!isOrderSummaryPage && imageUrl && (
        <Box sx={imageContainerSx}>
          <Box component="img" src={imageUrl} alt="success" />
        </Box>
      )}

      <DialogContent>
        <Box sx={dialogContentBoxSx}>
          <Typography variant="h5" fontWeight="500" sx={getTitleSx()}>
            {title}
          </Typography>
          <Typography fontSize="14px" sx={messageTypographySx}>
            {message}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          ...dialogActionsSx,
          justifyContent:
            isOrderSummaryPage || !hasSecondary ? "center" : "space-between",
        }}
      >
        {hasSecondary && (
          <Button
            onClick={secondaryAction}
            variant="outlined"
            sx={buttonStyles.outlined}
          >
            {!isOrderSummaryPage && (
              <Box component="img" src={backArrow} alt="back" sx={iconStyle} />
            )}
            {secondaryText}
          </Button>
        )}

        <Button
          onClick={primaryAction}
          variant="contained"
          sx={buttonStyles.contained}
        >
          {showIcon && (
            <Box
              component="img"
              src={iconSrc}
              alt={iconAlt}
              sx={truckIconStyle}
            />
          )}
          {primaryText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentDialog;
